import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { moneyFormat } from '@/lib/money';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft, Check, ChevronRight, Copy, Link2, Loader2, Plus, UserPlus, Users, X,
} from 'lucide-react';
import { HTTPError } from 'ky';
import { BottomSheet } from '@evi/ui';
import { toast } from '@/store/toast';
import {
  fetchMyHome, fetchMyChargers, fetchMyCharger, updateMyCharger, updateMyHome,
  fetchFriends, buildAvatarUrl, setMyHomeRate, setChargerAmperage,
  sendWhitelistInvite, fetchWhitelistInvites, revokeWhitelistInvite,
  fetchHomeWhitelist, updateHomeWhitelist,
  fetchHomeInviteLink, createHomeInviteLink, revokeHomeInviteLink,
  fetchHousehold, setHouseholdRole, removeHouseholdMember,
  type FriendCard, type HomeAddress, type HomeTenantView, type HomeRateSaveBody, type HomeRole,
} from '@/lib/api';
import { isGeographicHomeZone, sameCivilClock, type HomeCountry } from '@evi/shared';
import { HomeAddressFields } from '@/components/home/HomeAddressFields';
import {
  homeAddressDraftFrom, homeAddressErrorText, homeAddressForBody, type HomeAddressDraft,
} from '@/lib/homeAddressDraft';
import { SUPPORT_PATH, supportPathFrom } from '@/lib/supportLink';
import { deviceTimeZone, homeZoneLabel, homeZoneOptions } from '@/lib/homeTimeZone';
import type { HomeRateType, HomeTouWindow, HomeRateTier } from '@/lib/homeRate';
import {
  homeRateConfirmCents, isUtilityRateOverCeiling, utilityCeilingInDollars, utilityCeilingLabel,
  utilityCentsToDollars, utilityDollarsToCents,
} from '@/lib/utilityRateCeiling';
import { HomeRateConfirmStep } from '@/components/home/HomeRateConfirmStep';
import formStyles from '@/components/Form.module.css';
import styles from './MyHomePage.module.css';

interface Props { onNavigate: (path: string) => void }

const HOME_KEY = ['my-home'] as const;
const CHARGERS_KEY = ['my-chargers'] as const;

/**
 * Home Settings (Residential UX slice A). Configuration split out of My
 * Home, which stays the living dashboard: home profile (name, address,
 * charger label), the informational utility rate, charging speed, the
 * whitelist, and the live Monetize entry (deep-links into the charger
 * detail page's enrollment flow). Shares MyHomePage.module.css so both
 * pages keep one visual vocabulary.
 */
export function HomeSettingsPage({ onNavigate }: Props) {
  const { t } = useTranslation('homeSettings');
  const qc = useQueryClient();
  const homeQuery = useQuery({ queryKey: HOME_KEY, queryFn: fetchMyHome, staleTime: 60_000 });
  const home = homeQuery.data;
  const hasHome = Boolean(home?.tenant);
  const locationId = home?.location?._id ?? null;
  // Household roles: 'owner' (everything), 'manager' (edits settings),
  // 'viewer' (read-only). Missing role on a payload with a tenant is a
  // pre-slice owner response.
  const role: HomeRole = home?.role ?? 'owner';
  const isOwner = role === 'owner';
  const canEdit = role !== 'viewer';
  const homeChargerSummary = home?.charger ?? null;

  // Owner-scoped charger detail (amperage, display name). Members render
  // from the charger summary GET /me/home now carries.
  const chargersQuery = useQuery({
    queryKey: CHARGERS_KEY,
    queryFn: fetchMyChargers,
    staleTime: 60_000,
    enabled: hasHome && isOwner,
  });
  const homeCharger = locationId && isOwner
    ? (chargersQuery.data ?? []).find((c) => c.location_id === locationId) ?? null
    : null;

  const refreshHome = () => qc.invalidateQueries({ queryKey: HOME_KEY });

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <button
          type="button"
          className={styles.backFab}
          aria-label={t('back')}
          onClick={() => onNavigate('/my-home')}
        >
          <ArrowLeft size={16} />
        </button>
        <div className={styles.titleBlock}>
          <h1 className={styles.title}>{t('title')}</h1>
          <p className={styles.subtitle}>
            {homeQuery.isLoading
              ? t('loading')
              : isOwner
                ? t('subtitle.owner')
                : canEdit
                  ? t('subtitle.manager')
                  : t('subtitle.viewer')}
          </p>
        </div>
      </header>

      {homeQuery.isLoading && (
        <div className={styles.loading}><Loader2 size={18} className={styles.spin} /></div>
      )}

      {!homeQuery.isLoading && !hasHome && (
        <div className={styles.errorBox}>
          {t('noHome')}
        </div>
      )}

      {!homeQuery.isLoading && hasHome && (
        <>
          <HomeProfileSection
            homeName={home?.location?.name ?? home?.tenant?.name ?? ''}
            address={home?.location?.address ?? null}
            countryLocked={home?.location?.country_locked === true}
            charger={homeCharger ?? null}
            readOnly={!canEdit}
            onSaved={async () => {
              await refreshHome();
              await qc.invalidateQueries({ queryKey: CHARGERS_KEY });
            }}
            onNavigate={onNavigate}
          />
          <TimeZoneSection
            timezone={home?.location?.timezone ?? null}
            country={homeAddressDraftFrom(home?.location?.address).country}
            readOnly={!canEdit}
            onSaved={async () => {
              await refreshHome();
              await qc.invalidateQueries({ queryKey: CHARGERS_KEY });
              // Every charger detail ('my-charger', id): its guest-hours
              // notice reads the location's zone.
              await qc.invalidateQueries({ queryKey: ['my-charger'] });
            }}
          />
          <UtilityRateSection
            tenant={home?.tenant ?? null}
            currency={home?.location?.currency}
            readOnly={!canEdit}
            onRateChanged={refreshHome}
          />
          {isOwner && homeCharger && <AmperageSection chargerId={homeCharger._id} />}
          {(homeCharger || homeChargerSummary) && (
            <WhitelistSection
              canEdit={canEdit}
              onNavigate={onNavigate}
              chargerId={homeCharger?._id ?? null}
            />
          )}
          {canEdit && (homeCharger || homeChargerSummary) && <InviteLinkSection />}
          {canEdit && <HouseholdSection isOwner={isOwner} />}
          {isOwner && homeCharger && (
            <MonetizeSection
              chargerId={homeCharger._id}
              onNavigate={onNavigate}
            />
          )}
        </>
      )}
    </div>
  );
}

// ── Home profile: name, address, charger label ────────────────────────────
function HomeProfileSection({
  homeName, address, countryLocked, charger, readOnly, onSaved, onNavigate,
}: {
  homeName: string;
  address: HomeAddress | null;
  /** A change of country would be refused (GET /me/home country_locked). */
  countryLocked: boolean;
  charger: { _id: string; identity: string; display_name?: string | null } | null;
  /** Viewers see the values but cannot edit them. */
  readOnly?: boolean;
  onSaved: () => void | Promise<void>;
  onNavigate: (path: string) => void;
}) {
  const { t } = useTranslation('homeSettings');
  const { t: tAddress } = useTranslation('homeAddress');
  const [name, setName] = useState(homeName);
  // The address as HomeAddressFields edits it, country first. A stored
  // home with no country and an unrecognized state starts unpicked, so
  // the host must choose before the address can save.
  const stored = homeAddressDraftFrom(address);
  const [draft, setDraft] = useState<HomeAddressDraft>(stored);
  // Set by a refused save so every invalid field says why, typed or not
  // (a stored postal code that no longer passes, for instance).
  const [showAddressErrors, setShowAddressErrors] = useState(false);
  const [chargerName, setChargerName] = useState(charger?.display_name ?? '');
  const [saving, setSaving] = useState(false);
  // Set by a 409 HOME_COUNTRY_LOCKED: the refusal stays on the page, under
  // the address, with a Contact support button (the form at support_url),
  // rather than a toast that is gone before the host can act on it.
  const [countrySupportUrl, setCountrySupportUrl] = useState<string | null>(null);

  // Re-seed the form when the server copy lands or changes elsewhere.
  useEffect(() => { setName(homeName); }, [homeName]);
  useEffect(() => {
    setDraft(homeAddressDraftFrom(address));
    // A fresh server copy starts clean: errors show on leaving a field again.
    setShowAddressErrors(false);
  }, [address?.street, address?.city, address?.state, address?.zip, address?.country]);
  useEffect(() => { setChargerName(charger?.display_name ?? ''); }, [charger?.display_name]);

  const save = async (): Promise<void> => {
    const trimmedName = name.trim();
    const addressTouched = draft.country !== stored.country
      || draft.street.trim() !== stored.street
      || draft.city.trim() !== stored.city
      || draft.state.trim() !== stored.state
      || draft.zip.trim() !== stored.zip;
    const nextAddress = addressTouched ? homeAddressForBody(draft) : null;
    if (addressTouched && !nextAddress) {
      const filled = draft.country && draft.street.trim() && draft.city.trim()
        && draft.state.trim() && draft.zip.trim();
      setShowAddressErrors(true);
      toast(filled ? t('profile.toastCheckAddress') : t('profile.toastFillAddress'), { tone: 'warn' });
      return;
    }
    setSaving(true);
    setCountrySupportUrl(null);
    try {
      const nameChanged = trimmedName && trimmedName !== homeName;
      if (nameChanged || nextAddress) {
        await updateMyHome({
          ...(nameChanged ? { name: trimmedName } : {}),
          ...(nextAddress ? { address: nextAddress } : {}),
        });
      }
      const nextChargerName = chargerName.trim();
      if (charger && nextChargerName !== (charger.display_name ?? '')) {
        await updateMyCharger(charger._id, {
          display_name: nextChargerName.length > 0 ? nextChargerName : null,
        });
      }
      await onSaved();
      setShowAddressErrors(false);
      toast(t('profile.toastSaved'), { tone: 'accent' });
    } catch (err) {
      const envelope = await readErrorEnvelope(err);
      if (envelope.code === 'HOME_COUNTRY_LOCKED') {
        // Shown in the section, not toasted: the host needs the support
        // link. Locked since this page loaded: back to the saved country,
        // its region and postal code, and refetch so the pills hold still.
        setCountrySupportUrl(supportPathFrom(envelope.supportUrl));
        setDraft((d) => ({ ...d, country: stored.country, state: stored.state, zip: stored.zip }));
        void onSaved();
        return;
      }
      const text = (envelope.reason
        ? homeAddressErrorText(tAddress, envelope.reason, draft.country, draft.state)
        : null) ?? envelope.message ?? t('profile.toastError');
      toast(text, { tone: 'warn' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className={styles.guide}>
      <h2 className={styles.guideTitle}>{t('profile.title')}</h2>
      <p className={styles.guideIntro}>
        {t('profile.intro')}
      </p>

      <label className={styles.rateLabel} htmlFor="hs-name">{t('profile.name')}</label>
      <input
        id="hs-name"
        className={formStyles.input}
        type="text"
        maxLength={120}
        value={name}
        onChange={(e) => setName(e.target.value)}
        disabled={saving || readOnly}
      />

      <HomeAddressFields
        idPrefix="hs"
        value={draft}
        onChange={setDraft}
        disabled={saving || readOnly}
        showErrors={showAddressErrors}
        initial={stored}
        countryLocked={countryLocked}
        // The lock note says to contact support: this is the way there.
        // Left out while the refused-save notice below shows its own
        // Contact support button, so the page never shows two, and for a
        // viewer, who cannot change the home.
        countryLockAction={countrySupportUrl || readOnly ? undefined : (
          <button
            type="button"
            className={`${formStyles.btnGhost} ${styles.countryLockLink}`}
            onClick={() => onNavigate(SUPPORT_PATH)}
          >
            {t('profile.contactSupport')}
          </button>
        )}
      />

      {countrySupportUrl && (
        <div className={styles.countryLockNotice} role="alert">
          <p className={styles.countryLockText}>{t('profile.countryLocked')}</p>
          <button
            type="button"
            className={formStyles.btnSecondary}
            onClick={() => onNavigate(countrySupportUrl)}
          >
            {t('profile.contactSupport')}
          </button>
        </div>
      )}

      {charger && (
        <>
          <label className={styles.rateLabel} htmlFor="hs-charger-name">
            {t('profile.chargerName')}
          </label>
          <input
            id="hs-charger-name"
            className={formStyles.input}
            type="text"
            maxLength={80}
            value={chargerName}
            onChange={(e) => setChargerName(e.target.value)}
            placeholder={charger.identity}
            disabled={saving || readOnly}
          />
          <p className={styles.rateNote}>
            {t('profile.chargerNote', { serial: charger.identity })}
          </p>
        </>
      )}

      {!readOnly && (
        <button type="button" className={styles.registerCta} onClick={save} disabled={saving}>
          {saving ? <Loader2 size={15} className={styles.spin} /> : t('profile.save')}
        </button>
      )}
    </section>
  );
}

// ── Time zone (item 4, 2026-10-02) ────────────────────────────────────────
// The clock the home's guest windows mean (Location.timezone). Set from
// the phone at Set up My Home; changed here by the owner or a manager,
// either to this phone's zone in one tap or from the home country's zones
// (HOME_TIME_ZONES). A stored zone outside that list is still shown. A
// save refreshes the home, the chargers and each charger's detail, so the
// charger page's guest-hours notice says the new zone straight away.
function TimeZoneSection({
  timezone, country, readOnly, onSaved,
}: {
  timezone: string | null;
  country: HomeCountry | null;
  readOnly: boolean;
  onSaved: () => Promise<void>;
}) {
  const { t, i18n } = useTranslation('homeSettings');
  const [busy, setBusy] = useState(false);
  const phone = deviceTimeZone();
  const phoneZone = isGeographicHomeZone(phone) ? phone : null;
  // Offered only when it would change the clock, not just the city.
  const offerPhone = phoneZone !== null && (!timezone || !sameCivilClock(phoneZone, timezone));
  const options = homeZoneOptions(country, timezone);

  const save = async (zone: string): Promise<void> => {
    if (zone === timezone) return;
    setBusy(true);
    try {
      const res = await updateMyHome({ timezone: zone });
      await onSaved();
      toast(t('timezone.saved', { zone: homeZoneLabel(res.timezone ?? zone, i18n.language) }), { tone: 'accent' });
    } catch (err) {
      const { message } = await readErrorEnvelope(err);
      toast(message ?? t('timezone.error'), { tone: 'warn' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className={styles.guide}>
      <h2 className={styles.guideTitle}>{t('timezone.title')}</h2>
      <p className={styles.guideIntro}>{t('timezone.intro')}</p>
      <p className={styles.rateNote}>
        {timezone
          ? t('timezone.current', { zone: homeZoneLabel(timezone, i18n.language) })
          : readOnly ? t('timezone.notSetViewer') : t('timezone.notSet')}
      </p>
      {!readOnly && (
        <>
          {offerPhone && phoneZone && (
            <button
              type="button"
              className={styles.rateEditBtn}
              onClick={() => { void save(phoneZone); }}
              disabled={busy}
            >
              {busy ? <Loader2 size={13} className={styles.spin} /> : t('timezone.usePhone', { zone: homeZoneLabel(phoneZone, i18n.language) })}
            </button>
          )}
          <label className={styles.rateLabel} htmlFor="hs-timezone">{t('timezone.selectLabel')}</label>
          <select
            id="hs-timezone"
            className={formStyles.input}
            value={timezone ?? ''}
            onChange={(e) => { if (e.target.value) void save(e.target.value); }}
            disabled={busy}
          >
            {!timezone && <option value="" disabled>{t('timezone.choose')}</option>}
            {options.map((zone) => (
              <option key={zone} value={zone}>{homeZoneLabel(zone, i18n.language)}</option>
            ))}
          </select>
        </>
      )}
    </section>
  );
}

/** The `{ error: { code, message, reason, support_url } }` envelope off a
 *  ky HTTPError; nulls when the error is not shaped like that (network
 *  drop). */
async function readErrorEnvelope(
  err: unknown,
): Promise<{ code: string | null; message: string | null; reason: string | null; supportUrl: string | null }> {
  const empty = { code: null, message: null, reason: null, supportUrl: null };
  const response = (err as { response?: Response }).response;
  if (!response) return empty;
  try {
    const body = await response.clone().json() as {
      error?: { code?: string; message?: string; reason?: string; support_url?: string };
    };
    return {
      code: body.error?.code ?? null,
      message: body.error?.message ?? null,
      reason: body.error?.reason ?? null,
      supportUrl: typeof body.error?.support_url === 'string' ? body.error.support_url : null,
    };
  } catch {
    return empty;
  }
}

// ── Utility rate (informational; flat / time-of-use / tiered) ─────────────
const RATE_TYPES: Array<{ key: HomeRateType; labelKey: string }> = [
  { key: 'flat', labelKey: 'rate.type.flat' },
  { key: 'time_of_use', labelKey: 'rate.type.timeOfUse' },
  { key: 'tiered', labelKey: 'rate.type.tiered' },
];

const MAX_TOU_WINDOWS = 6;
const MAX_TIERS = 6;

/** "12 AM" … "11 PM"; 24 renders as 12 AM (midnight, end-of-day). */
function hourLabel(h: number): string {
  const t = h % 24;
  const disp = t % 12 === 0 ? 12 : t % 12;
  return `${disp} ${t < 12 ? 'AM' : 'PM'}`;
}

/** Dollars string -> cents to a hundredth of a cent ($0.4363 is 43.63¢); null when blank or invalid. Warren, 21
 *  October 2026, night: "like the rest. The location form stores rates to a hundredth of a cent, so 43.63 stays 43.63." */
const dollarsToCents = utilityDollarsToCents;
/** Stored cents back into a $ field: "0.17", "0.4363". */
const centsToDollars = utilityCentsToDollars;

interface WindowDraft { start: number; end: number; rate: string; days: number[] }

/** 0=Sun..6=Sat — same order and convention as the guest time-window
 *  day chips (TimeWindowRows). Empty selection = every day. */
const RATE_DAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'] as const;
interface TierDraft { upTo: string; rate: string }

function seedWindows(tenant: HomeTenantView | null): WindowDraft[] {
  const saved = tenant?.owner_utility_tou_windows ?? [];
  if (saved.length > 0) {
    return saved.map((w) => ({
      start: w.start_hour, end: w.end_hour, rate: centsToDollars(w.rate_cents_per_kwh),
      days: w.days_of_week ?? [],
    }));
  }
  // A sensible first row: the common late-afternoon peak.
  return [{ start: 16, end: 21, rate: '', days: [] }];
}

function seedTiers(tenant: HomeTenantView | null): TierDraft[] {
  const saved = tenant?.owner_utility_tiers ?? [];
  if (saved.length > 0) {
    return saved.map((t) => ({
      upTo: t.up_to_kwh != null ? String(t.up_to_kwh) : '',
      rate: centsToDollars(t.rate_cents_per_kwh),
    }));
  }
  return [{ upTo: '300', rate: '' }, { upTo: '', rate: '' }];
}

function UtilityRateSection({
  tenant, currency, readOnly, onRateChanged,
}: {
  tenant: HomeTenantView | null;
  /** The home's currency (item 3): the rate is entered and shown in it.
   *  Absent from an older Api: USD. */
  currency?: string | null;
  /** Viewers see the structure but cannot edit it. */
  readOnly?: boolean;
  onRateChanged: () => void | Promise<void>;
}) {
  const { t, i18n } = useTranslation('homeSettings');
  // CA$ at a Canadian home, $ at a US one (item 3).
  const m = moneyFormat(currency, i18n.language);
  const savedType = tenant?.owner_utility_rate_type ?? 'flat';
  const savedBase = tenant?.owner_utility_rate_cents_per_kwh ?? null;
  const [rateType, setRateType] = useState<HomeRateType>(savedType);
  const [rateInput, setRateInput] = useState(centsToDollars(savedBase));
  const [windows, setWindows] = useState<WindowDraft[]>(() => seedWindows(tenant));
  const [tiers, setTiers] = useState<TierDraft[]>(() => seedTiers(tenant));
  const [saving, setSaving] = useState(false);
  // The second step (Warren, 21 October 2026, night): the rate above 40¢ being confirmed, or null. Any edit to a rate
  // takes it away, so Confirm always saves what the step names.
  const [confirmCents, setConfirmCents] = useState<number | null>(null);
  useEffect(() => { setConfirmCents(null); }, [rateType, rateInput, windows, tiers]);

  // Re-seed only when the SAVED copy actually changes (post-save refetch or
  // an edit elsewhere) — not on every home refetch, which would clobber
  // in-progress edits.
  const savedSig = JSON.stringify([
    savedType, savedBase, tenant?.owner_utility_tou_windows, tenant?.owner_utility_tiers,
  ]);
  useEffect(() => {
    setRateType(savedType);
    setRateInput(centsToDollars(savedBase));
    setWindows(seedWindows(tenant));
    setTiers(seedTiers(tenant));
    // savedSig serializes every saved field, so it is the whole dependency set.
  }, [savedSig]);

  const updateWindow = (i: number, patch: Partial<WindowDraft>): void =>
    setWindows((prev) => prev.map((w, j) => (j === i ? { ...w, ...patch } : w)));
  const updateTier = (i: number, patch: Partial<TierDraft>): void =>
    setTiers((prev) => prev.map((t, j) => (j === i ? { ...t, ...patch } : t)));

  // The Site electricity constants on this rate (Warren, 2026-09-27: "40
  // flat, 70 on a TOU window", the TOU cap since raised to 80; a bracket is
  // a flat rate for its band). Flat and bracket rates cap at 60 since 21
  // October 2026, night ("a homeowner's own rate cap go to 60 cents, like
  // the rest"), above 40 with the second step below.
  // The API refuses the same, but a refused save here only toasts a
  // generic error, so each field says so inline and Save holds. The base
  // is not checked in tiered mode: that save re-sends the stored base,
  // which the tiered editor hides.
  const baseOver = rateType !== 'tiered' && isUtilityRateOverCeiling(dollarsToCents(rateInput), 'flat');
  const windowOver = windows.map((w) => rateType === 'time_of_use' && isUtilityRateOverCeiling(dollarsToCents(w.rate), 'window'));
  const tierOver = tiers.map((tier) => rateType === 'tiered' && isUtilityRateOverCeiling(dollarsToCents(tier.rate), 'tier'));
  const overCeiling = baseOver || windowOver.some(Boolean) || tierOver.some(Boolean);

  const save = async (confirmed = false): Promise<void> => {
    // Save is disabled while a rate is over its cap; this covers any
    // other way in.
    if (overCeiling) return;
    let body: HomeRateSaveBody;
    if (rateType === 'flat') {
      const cents = dollarsToCents(rateInput);
      if (rateInput.trim() !== '' && cents == null) {
        toast(t('rate.toastValidRate'), { tone: 'warn' });
        return;
      }
      body = { rate_type: 'flat', rate_cents_per_kwh: cents };
    } else if (rateType === 'time_of_use') {
      const base = dollarsToCents(rateInput);
      if (base == null) {
        toast(t('rate.toastBaseRate'), { tone: 'warn' });
        return;
      }
      const mapped: HomeTouWindow[] = [];
      for (const w of windows) {
        const cents = dollarsToCents(w.rate);
        if (cents == null) {
          toast(t('rate.toastWindowRate'), { tone: 'warn' });
          return;
        }
        // end at-or-before start = crosses midnight (Ontario's
        // 7 PM–7 AM off-peak). Only a zero-length window is invalid.
        if (w.end === w.start) {
          toast(t('rate.toastWindowEnd'), { tone: 'warn' });
          return;
        }
        // All 7 selected is canonically "every day" — store it as the
        // absent field, same as no selection.
        mapped.push({
          start_hour: w.start,
          end_hour: w.end,
          rate_cents_per_kwh: cents,
          ...(w.days.length > 0 && w.days.length < 7 ? { days_of_week: [...w.days].sort((a, b) => a - b) } : {}),
        });
      }
      // 7x24 slot-grid overlap check — mirrors the server. Overlap
      // only matters where two windows share a calendar day AND an
      // hour; wrap windows expand to [start,24)+[0,end); empty days
      // = every day.
      const slotTaken = new Array<boolean>(7 * 24).fill(false);
      for (const w of mapped) {
        const hours = w.end_hour > w.start_hour
          ? Array.from({ length: w.end_hour - w.start_hour }, (_, k) => w.start_hour + k)
          : [
            ...Array.from({ length: 24 - w.start_hour }, (_, k) => w.start_hour + k),
            ...Array.from({ length: w.end_hour }, (_, k) => k),
          ];
        const days = w.days_of_week?.length ? w.days_of_week : [0, 1, 2, 3, 4, 5, 6];
        for (const d of days) {
          for (const h of hours) {
            if (slotTaken[d * 24 + h]) {
              toast(t('rate.toastWindowOverlap'), { tone: 'warn' });
              return;
            }
            slotTaken[d * 24 + h] = true;
          }
        }
      }
      body = { rate_type: 'time_of_use', rate_cents_per_kwh: base, tou_windows: mapped };
    } else {
      const mapped: HomeRateTier[] = [];
      for (let i = 0; i < tiers.length; i += 1) {
        const tier = tiers[i]!;
        const cents = dollarsToCents(tier.rate);
        if (cents == null) {
          toast(t('rate.toastBracketRate'), { tone: 'warn' });
          return;
        }
        if (i === tiers.length - 1) {
          mapped.push({ up_to_kwh: null, rate_cents_per_kwh: cents });
          continue;
        }
        const upTo = Number(tier.upTo);
        if (!Number.isFinite(upTo) || upTo <= 0) {
          toast(t('rate.toastBracketBoundary'), { tone: 'warn' });
          return;
        }
        const prev = i > 0 ? Number(tiers[i - 1]!.upTo) : 0;
        if (upTo <= prev) {
          toast(t('rate.toastBracketIncrease'), { tone: 'warn' });
          return;
        }
        mapped.push({ up_to_kwh: upTo, rate_cents_per_kwh: cents });
      }
      // The tiered editor has no base-rate field; keep the stored base so
      // switching back to flat later finds it untouched.
      body = { rate_type: 'tiered', rate_cents_per_kwh: savedBase, tiers: mapped };
    }
    // The second step first (Warren, 21 October 2026, night): a flat, base or bracket rate above 40¢ that is not the one
    // already saved. Its Confirm comes back here confirmed; the API asks the same without the flag.
    const asked = homeRateConfirmCents(body, { rate_cents_per_kwh: savedBase, tiers: tenant?.owner_utility_tiers ?? null });
    if (asked !== null && !confirmed) {
      setConfirmCents(asked);
      return;
    }
    if (asked !== null) body = { ...body, confirm_above_40: true };
    setSaving(true);
    try {
      await setMyHomeRate(body);
      setConfirmCents(null);
      await onRateChanged();
      toast(t('rate.toastSaved'), { tone: 'accent' });
    } catch (err) {
      toast(err instanceof Error ? err.message : t('rate.toastError'), { tone: 'warn' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className={styles.guide}>
      <h2 className={styles.guideTitle}>{t('rate.title')}</h2>
      <p className={styles.guideIntro}>
        {t('rate.intro')}
      </p>
      {/* Reverse breadcrumb of the charger page's margin line — the
          two rates live on different pages by scope (property vs
          device); this line is the seam that connects them. */}
      <p className={styles.guideIntro}>
        {t('rate.usedFor')}
        {' '}{t('rate.ledgerNote')}
      </p>

      <div className={styles.rateTypeRow} role="radiogroup" aria-label={t('rate.structureAria')}>
        {RATE_TYPES.map((rt) => (
          <button
            key={rt.key}
            type="button"
            role="radio"
            aria-checked={rateType === rt.key}
            className={rateType === rt.key ? styles.periodChipActive : styles.periodChip}
            onClick={() => setRateType(rt.key)}
            disabled={saving || readOnly}
          >
            {t(rt.labelKey)}
          </button>
        ))}
      </div>

      {rateType !== 'tiered' && (
        <>
          <label className={styles.rateLabel} htmlFor="hs-rate">
            {rateType === 'flat'
              ? t('rate.labelFlat', { symbol: m.symbol })
              : t('rate.labelBase', { symbol: m.symbol })}
          </label>
          <div className={styles.pricingInputGroup}>
            <span className={styles.pricingPrefix} aria-hidden>{m.symbol}</span>
            <input
              id="hs-rate"
              className={m.symbol.length > 1 ? `${styles.pricingInput} ${styles.pricingInputWide}` : styles.pricingInput}
              type="number"
              inputMode="decimal"
              step="0.0001"
              min={0}
              max={utilityCeilingInDollars('flat')}
              value={rateInput}
              onChange={(e) => setRateInput(e.target.value)}
              placeholder={t('rate.placeholderRate')}
              disabled={saving || readOnly}
              aria-invalid={baseOver || undefined}
              aria-describedby={baseOver ? 'hs-rate-ceiling' : undefined}
            />
            <span className={styles.pricingSuffix} aria-hidden>/kWh</span>
          </div>
          {baseOver && (
            <p id="hs-rate-ceiling" className={formStyles.errorMsg} role="alert">
              {t('rate.ceilingFlat', { max: utilityCeilingLabel('flat', m.currency, m.locale) })}
            </p>
          )}
        </>
      )}

      {rateType === 'time_of_use' && (
        <div className={styles.rateRows}>
          <span className={styles.rateLabel}>{t('rate.windows')}</span>
          <p className={styles.guideIntro}>{t('rate.wrapHint')}</p>
          <p className={styles.guideIntro}>{t('rate.daysHint')}</p>
          {windows.map((w, i) => (
            <div key={i} className={styles.touWindow}>
              <div className={styles.dayChips}>
                {RATE_DAY_KEYS.map((dayKey, dow) => {
                  const active = w.days.includes(dow);
                  return (
                    <button
                      type="button"
                      key={dayKey}
                      className={active ? styles.dayChipActive : styles.dayChip}
                      aria-pressed={active}
                      disabled={saving || readOnly}
                      onClick={() => updateWindow(i, {
                        days: active
                          ? w.days.filter((d) => d !== dow)
                          : [...w.days, dow].sort((a, b) => a - b),
                      })}
                    >
                      {t(`rate.days.${dayKey}`)}
                    </button>
                  );
                })}
              </div>
              <div className={styles.touRow}>
              <select
                className={formStyles.input}
                value={w.start}
                aria-label={t('rate.windowStartAria')}
                onChange={(e) => updateWindow(i, { start: Number(e.target.value) })}
                disabled={saving || readOnly}
              >
                {Array.from({ length: 24 }, (_, h) => (
                  <option key={h} value={h}>{hourLabel(h)}</option>
                ))}
              </select>
              <select
                className={formStyles.input}
                value={w.end}
                aria-label={t('rate.windowEndAria')}
                onChange={(e) => updateWindow(i, { end: Number(e.target.value) })}
                disabled={saving || readOnly}
              >
                {Array.from({ length: 24 }, (_, idx) => idx + 1).map((h) => (
                  <option key={h} value={h}>{hourLabel(h)}</option>
                ))}
              </select>
              <input
                className={formStyles.input}
                type="number"
                inputMode="decimal"
                step="0.0001"
                min={0}
                max={utilityCeilingInDollars('window')}
                value={w.rate}
                placeholder={`${m.symbol}/kWh`}
                aria-label={t('rate.windowRateAria')}
                aria-invalid={windowOver[i] || undefined}
                aria-describedby={windowOver[i] ? `hs-window-ceiling-${i}` : undefined}
                onChange={(e) => updateWindow(i, { rate: e.target.value })}
                disabled={saving || readOnly}
              />
              <button
                type="button"
                className={styles.wlRemove}
                aria-label={t('rate.removeWindowAria')}
                onClick={() => setWindows(windows.filter((_, j) => j !== i))}
                disabled={saving || readOnly || windows.length <= 1}
              >
                <X size={15} />
              </button>
              </div>
              {windowOver[i] && (
                <p id={`hs-window-ceiling-${i}`} className={formStyles.errorMsg} role="alert">
                  {t('rate.ceilingWindow', { max: utilityCeilingLabel('window', m.currency, m.locale) })}
                </p>
              )}
            </div>
          ))}
          {windows.length < MAX_TOU_WINDOWS && (
            <button
              type="button"
              className={styles.whitelistAdd}
              onClick={() => setWindows([...windows, { start: 16, end: 21, rate: '', days: [] }])}
              disabled={saving || readOnly}
            >
              <Plus size={15} /> {t('rate.addWindow')}
            </button>
          )}
          <p className={styles.rateNote}>
            {t('rate.windowsNote')}
          </p>
        </div>
      )}

      {rateType === 'tiered' && (
        <div className={styles.rateRows}>
          <span className={styles.rateLabel}>{t('rate.brackets')}</span>
          {tiers.map((tier, i) => {
            const isLast = i === tiers.length - 1;
            return (
              <div key={i} className={styles.tierRow}>
                {isLast
                  ? <span className={styles.tierAbove}>{t('rate.everythingAbove')}</span>
                  : (
                    <input
                      className={formStyles.input}
                      type="number"
                      inputMode="decimal"
                      min={0}
                      value={tier.upTo}
                      placeholder={t('rate.upToPlaceholder')}
                      aria-label={t('rate.bracketBoundaryAria')}
                      onChange={(e) => updateTier(i, { upTo: e.target.value })}
                      disabled={saving || readOnly}
                    />
                  )}
                <input
                  className={formStyles.input}
                  type="number"
                  inputMode="decimal"
                  step="0.0001"
                  min={0}
                  max={utilityCeilingInDollars('tier')}
                  value={tier.rate}
                  placeholder={`${m.symbol}/kWh`}
                  aria-label={t('rate.bracketRateAria')}
                  aria-invalid={tierOver[i] || undefined}
                  aria-describedby={tierOver[i] ? 'hs-tier-ceiling' : undefined}
                  onChange={(e) => updateTier(i, { rate: e.target.value })}
                  disabled={saving || readOnly}
                />
                <button
                  type="button"
                  className={styles.wlRemove}
                  aria-label={t('rate.removeBracketAria')}
                  onClick={() => setTiers(tiers.filter((_, j) => j !== i))}
                  disabled={saving || readOnly || isLast}
                >
                  <X size={15} />
                </button>
              </div>
            );
          })}
          {tierOver.some(Boolean) && (
            <p id="hs-tier-ceiling" className={formStyles.errorMsg} role="alert">
              {t('rate.ceilingBracket', { max: utilityCeilingLabel('tier', m.currency, m.locale) })}
            </p>
          )}
          {tiers.length < MAX_TIERS && (
            <button
              type="button"
              className={styles.whitelistAdd}
              onClick={() => setTiers([...tiers.slice(0, -1), { upTo: '', rate: '' }, tiers[tiers.length - 1]!])}
              disabled={saving || readOnly}
            >
              <Plus size={15} /> {t('rate.addBracket')}
            </button>
          )}
          <p className={styles.rateNote}>
            {t('rate.bracketsNote')}
          </p>
        </div>
      )}

      {!readOnly && confirmCents !== null && (
        <HomeRateConfirmStep cents={confirmCents} currency={m.currency} busy={saving}
          onConfirm={() => { void save(true); }} onCancel={() => setConfirmCents(null)} />
      )}

      {!readOnly && (
        <button type="button" className={styles.registerCta} onClick={() => { void save(); }}
          disabled={saving || overCeiling || confirmCents !== null}>
          {saving ? <Loader2 size={15} className={styles.spin} /> : t('rate.save')}
        </button>
      )}
    </section>
  );
}

// ── Monetize (slice D) — live entry into earning. Not yet enrolled: the
// row deep-links to the earn walk at /my-chargers/:id/earn (price,
// hours, payouts in one pass). Enrolled: the charger detail page, where
// the price and guest settings live (the same surface multifamily
// residents use; the page renders its home branch there). Enrolment is
// read off the charger detail on the same query key AmperageSection
// uses, so the two sections share one fetch; until it resolves — or if
// it fails — the row keeps today's charger-page target, which shows the
// earn card and links on to the walk itself.
// The one section with a glow: the earnings opportunity should stand
// out from the config cards around it.
function MonetizeSection({
  chargerId, onNavigate,
}: { chargerId: string; onNavigate: (path: string) => void }) {
  const { t } = useTranslation('homeSettings');
  const detailQ = useQuery({
    queryKey: ['my-charger', chargerId],
    queryFn: () => fetchMyCharger(chargerId),
    staleTime: 60_000,
  });
  const home = detailQ.data?.home_monetization ?? null;
  const target = home && !home.enrolled
    ? `/my-chargers/${chargerId}/earn`
    : `/my-chargers/${chargerId}`;
  return (
    <section className={`${styles.guide} ${styles.monetizeGlow}`}>
      <button
        type="button"
        className={styles.monetizeRow}
        onClick={() => onNavigate(target)}
      >
        <div>
          <h2 className={styles.guideTitle}>{t('monetize.title')}</h2>
          <p className={styles.guideIntro}>
            {t('monetize.intro')}
          </p>
        </div>
        <ChevronRight size={16} className={styles.monetizeChevron} aria-hidden />
      </button>
    </section>
  );
}

// ── Charging speed (moved unchanged from My Home) ─────────────────────────
function AmperageSection({ chargerId }: { chargerId: string }) {
  const { t } = useTranslation('homeSettings');
  const qc = useQueryClient();
  const detailQ = useQuery({
    queryKey: ['my-charger', chargerId],
    queryFn: () => fetchMyCharger(chargerId),
    staleTime: 60_000,
  });
  const detail = detailQ.data;
  // Rated ceiling from the connector's max_current_a; home chargers usually omit
  // it, so fall back to a conservative single-phase residential max.
  const ratedMax = detail?.connectors?.[0]?.max_current_a ?? 48;
  const currentCap = detail?.owner_amperage_cap_a ?? null;
  const isOnline = detail?.is_online ?? false;

  const [ampsInput, setAmpsInput] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setAmpsInput(String(currentCap ?? ratedMax));
  }, [currentCap, ratedMax]);

  const isOffline = (err: unknown): boolean =>
    err instanceof HTTPError && err.response.status === 409;

  const apply = async (): Promise<void> => {
    const amps = Math.round(Number(ampsInput));
    if (!Number.isFinite(amps) || amps < 6 || amps > ratedMax) {
      toast(t('amperage.toastRange', { max: ratedMax }), { tone: 'warn' });
      return;
    }
    setBusy(true);
    try {
      await setChargerAmperage(chargerId, amps);
      await qc.invalidateQueries({ queryKey: ['my-charger', chargerId] });
      toast(t('amperage.toastCapped', { amps }), { tone: 'accent' });
    } catch (err) {
      toast(isOffline(err)
        ? t('amperage.toastOffline')
        : t('amperage.toastApplyError'), { tone: 'warn' });
    } finally {
      setBusy(false);
    }
  };

  const clearCap = async (): Promise<void> => {
    setBusy(true);
    try {
      await setChargerAmperage(chargerId, null);
      await qc.invalidateQueries({ queryKey: ['my-charger', chargerId] });
      toast(t('amperage.toastCleared'), { tone: 'accent' });
    } catch (err) {
      toast(isOffline(err)
        ? t('amperage.toastOffline')
        : t('amperage.toastClearError'), { tone: 'warn' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className={styles.guide}>
      <h2 className={styles.guideTitle}>{t('amperage.title')}</h2>
      <p className={styles.guideIntro}>
        {t('amperage.intro')}
      </p>

      {detailQ.isLoading && <p className={styles.whitelistMuted}>{t('loading')}</p>}

      {!detailQ.isLoading && !isOnline && (
        <p className={styles.whitelistMuted}>
          {t('amperage.offline')}
        </p>
      )}

      {!detailQ.isLoading && isOnline && (
        <>
          {currentCap != null && (
            <p className={styles.ampCurrent}>
              {t('amperage.currentCapPrefix')} <strong>{currentCap}A</strong> {t('amperage.currentCapOf', { max: ratedMax })}
            </p>
          )}
          <div className={styles.ampRow}>
            <input
              className={formStyles.input}
              type="number"
              inputMode="numeric"
              min={6}
              max={ratedMax}
              step={1}
              value={ampsInput}
              onChange={(e) => setAmpsInput(e.target.value)}
              disabled={busy}
              aria-label={t('amperage.inputAria')}
            />
            <span className={styles.ampUnit}>A</span>
            <button type="button" className={styles.ampApply} onClick={apply} disabled={busy}>
              {busy ? <Loader2 size={14} className={styles.spin} /> : t('amperage.apply')}
            </button>
          </div>
          <p className={styles.rateNote}>{t('amperage.range', { max: ratedMax })}</p>
          {currentCap != null && (
            <button type="button" className={styles.rateEditBtn} onClick={clearCap} disabled={busy}>
              {t('amperage.clear')}
            </button>
          )}
        </>
      )}
    </section>
  );
}

// ── Whitelist (home-scoped; owners AND household managers) ────────────────
const WHITELIST_KEY = ['home-whitelist'] as const;

function WhitelistSection({
  canEdit, onNavigate, chargerId,
}: {
  canEdit: boolean;
  onNavigate: (path: string) => void;
  chargerId?: string | null;
}) {
  const { t } = useTranslation('homeSettings');
  const qc = useQueryClient();
  // Home-scoped read: names hydrate server-side (pass-list entries are
  // not necessarily the viewer's friends), and managers work without the
  // owner-only /my-chargers routes.
  const listQ = useQuery({
    queryKey: WHITELIST_KEY,
    queryFn: fetchHomeWhitelist,
    staleTime: 30_000,
  });
  const friends = useQuery({
    queryKey: ['friends'],
    queryFn: fetchFriends,
    staleTime: 60_000,
    enabled: canEdit,
  });
  const [pickerOpen, setPickerOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const members = listQ.data?.members ?? [];
  const passIds = members.map((m) => m.user_id);
  const nameById = new Map(members.map((m) => [m.user_id, m.name ?? m.email]));
  const accepted = friends.data?.accepted ?? [];
  const friendByUserId = new Map(accepted.map((f) => [f.user.id, f.user]));
  const addable = accepted.filter((f) => !passIds.includes(f.user.id));

  // One incremental op per action through the home-scoped PATCH — never
  // a full-array replace, so a member who joined via the invite link
  // moments ago can't be wiped by this page's stale snapshot. Only
  // guest_pass_list is written server-side; is_open_to_guests is left
  // untouched, so the sharing-activation Connect gate never fires and
  // the list stays free.
  const persist = async (ops: { add?: string[]; remove?: string[] }): Promise<boolean> => {
    setBusy(true);
    try {
      await updateHomeWhitelist(ops);
      await qc.invalidateQueries({ queryKey: WHITELIST_KEY });
      return true;
    } catch (err) {
      toast(err instanceof Error ? err.message : t('whitelist.toastUpdateError'), { tone: 'warn' });
      return false;
    } finally {
      setBusy(false);
    }
  };

  const addFriend = async (userId: string): Promise<void> => {
    setPickerOpen(false);
    if (await persist({ add: [userId] })) toast(t('whitelist.toastAdded'), { tone: 'accent' });
  };
  const removeFromWhitelist = async (userId: string): Promise<void> => {
    if (await persist({ remove: [userId] })) {
      toast(t('whitelist.toastRemoved'), { tone: 'accent' });
    }
  };

  return (
    <section className={styles.guide}>
      <div className={styles.whitelistHead}>
        <h2 className={styles.guideTitle}>{t('whitelist.title')}</h2>
        {canEdit && (
          <button
            type="button"
            className={styles.whitelistAdd}
            onClick={() => setPickerOpen(true)}
            disabled={busy || listQ.isLoading}
          >
            <UserPlus size={15} /> {t('whitelist.addFriend')}
          </button>
        )}
      </div>
      {/* Same list as the charger page's Free pass list — both edit
          Charger.guest_pass_list. Say so, or the two surfaces read as
          two different lists. */}
      {chargerId && (
        <p className={styles.guideIntro}>
          {t('whitelist.alsoOn')}{' '}
          <button
            type="button"
            className={styles.crossLink}
            onClick={() => onNavigate(`/my-chargers/${chargerId}`)}
          >
            {t('whitelist.alsoOnLink')}
          </button>
        </p>
      )}
      <p className={styles.guideIntro}>
        {t('whitelist.intro')}
      </p>

      {canEdit && <WhitelistEmailAdd />}

      {listQ.isLoading && <p className={styles.whitelistMuted}>{t('loading')}</p>}

      {!listQ.isLoading && listQ.error && (
        <div className={styles.errorBox}>{t('whitelist.loadError')}</div>
      )}

      {!listQ.isLoading && !listQ.error && passIds.length === 0 && (
        <p className={styles.whitelistMuted}>
          {t('whitelist.empty')}
        </p>
      )}

      {!listQ.isLoading && !listQ.error && passIds.length > 0 && (
        <ul className={styles.whitelist}>
          {passIds.map((id) => (
            <WhitelistRow
              key={id}
              user={friendByUserId.get(id) ?? null}
              fallbackName={nameById.get(id) ?? null}
              busy={busy}
              canRemove={canEdit}
              onRemove={() => removeFromWhitelist(id)}
            />
          ))}
        </ul>
      )}

      {canEdit && <PendingInvitesList />}

      <WhitelistPicker
        isOpen={pickerOpen}
        addable={addable}
        hasFriends={accepted.length > 0}
        friendsLoading={friends.isLoading}
        busy={busy}
        onClose={() => setPickerOpen(false)}
        onPick={addFriend}
        onGoToFriends={() => { setPickerOpen(false); onNavigate('/friends'); }}
      />
    </section>
  );
}

// ── Invite link (Residential household slice) ─────────────────────────────
// One multi-use link for the household. Hash-at-rest server-side: the URL
// shows exactly once at mint — share it right away; mint again for a new
// one (which turns off the old, the leak kill switch).
const INVITE_LINK_KEY = ['home-invite-link'] as const;

function InviteLinkSection() {
  const { t } = useTranslation('homeSettings');
  const qc = useQueryClient();
  const metaQ = useQuery({
    queryKey: INVITE_LINK_KEY,
    queryFn: fetchHomeInviteLink,
    staleTime: 30_000,
  });
  const meta = metaQ.data;
  const [mintedUrl, setMintedUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const mint = async (): Promise<void> => {
    setBusy(true);
    try {
      const res = await createHomeInviteLink();
      setMintedUrl(res.url);
      setCopied(false);
      await qc.invalidateQueries({ queryKey: INVITE_LINK_KEY });
    } catch {
      toast(t('invite.toastCreateError'), { tone: 'warn' });
    } finally {
      setBusy(false);
    }
  };

  const copy = async (): Promise<void> => {
    if (!mintedUrl) return;
    try {
      await navigator.clipboard.writeText(mintedUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast(t('invite.toastCopyError'), { tone: 'warn' });
    }
  };

  const share = async (): Promise<void> => {
    if (!mintedUrl) return;
    try {
      await navigator.share({ title: t('invite.shareTitle'), url: mintedUrl });
    } catch {
      // Dismissed the share sheet — nothing to do.
    }
  };

  const revoke = async (): Promise<void> => {
    setBusy(true);
    try {
      await revokeHomeInviteLink();
      setMintedUrl(null);
      await qc.invalidateQueries({ queryKey: INVITE_LINK_KEY });
      toast(t('invite.toastTurnedOff'), { tone: 'accent' });
    } catch {
      toast(t('invite.toastTurnOffError'), { tone: 'warn' });
    } finally {
      setBusy(false);
    }
  };

  const hasActive = mintedUrl != null || meta?.active === true;

  return (
    <section className={styles.guide}>
      <h2 className={styles.guideTitle}><Link2 size={15} aria-hidden /> {t('invite.title')}</h2>
      <p className={styles.guideIntro}>
        {t('invite.intro')}
      </p>

      {mintedUrl ? (
        <div className={styles.copyField}>
          <span className={styles.copyLabel}>{t('invite.shareLabel')}</span>
          <div className={styles.copyRow}>
            <span className={styles.copyValue}>{mintedUrl}</span>
            <button type="button" className={styles.copyBtn} onClick={() => { void copy(); }}>
              {copied ? <Check size={13} /> : <Copy size={13} />} {copied ? t('invite.copied') : t('invite.copy')}
            </button>
          </div>
          {typeof navigator.share === 'function' && (
            <button type="button" className={styles.registerCta} onClick={() => { void share(); }}>
              {t('invite.share')}
            </button>
          )}
        </div>
      ) : meta?.active ? (
        <p className={styles.whitelistMuted}>
          {typeof meta.use_count === 'number' && meta.use_count > 0
            ? t('invite.activeUsed', { count: meta.use_count })
            : t('invite.active')}
        </p>
      ) : (
        <p className={styles.whitelistMuted}>{t('invite.noLink')}</p>
      )}

      {!mintedUrl && (
        <button type="button" className={styles.registerCta} onClick={() => { void mint(); }} disabled={busy}>
          {busy
            ? <Loader2 size={15} className={styles.spin} />
            : hasActive ? t('invite.createNew') : t('invite.create')}
        </button>
      )}
      {hasActive && (
        <button type="button" className={styles.rateEditBtn} onClick={() => { void revoke(); }} disabled={busy}>
          {t('invite.turnOff')}
        </button>
      )}
    </section>
  );
}

// ── Household (Residential household slice) ───────────────────────────────
// Members who joined through the invite link. The OWNER controls roles
// and removal; managers see the list. Removing someone here removes page
// access only — charging stays on the free pass list above.
const HOUSEHOLD_KEY = ['home-household'] as const;

function HouseholdSection({ isOwner }: { isOwner: boolean }) {
  const { t } = useTranslation('homeSettings');
  const qc = useQueryClient();
  const membersQ = useQuery({
    queryKey: HOUSEHOLD_KEY,
    queryFn: fetchHousehold,
    staleTime: 30_000,
  });
  const [busyId, setBusyId] = useState<string | null>(null);
  const members = membersQ.data ?? [];

  const changeRole = async (userId: string, role: 'manager' | 'viewer'): Promise<void> => {
    setBusyId(userId);
    try {
      await setHouseholdRole(userId, role);
      await qc.invalidateQueries({ queryKey: HOUSEHOLD_KEY });
      toast(role === 'manager' ? t('household.toastCanEdit') : t('household.toastViewOnly'), { tone: 'accent' });
    } catch {
      toast(t('household.toastRoleError'), { tone: 'warn' });
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (userId: string, name: string): Promise<void> => {
    setBusyId(userId);
    try {
      await removeHouseholdMember(userId);
      await qc.invalidateQueries({ queryKey: HOUSEHOLD_KEY });
      toast(t('household.toastRemoved', { name }), { tone: 'accent' });
    } catch {
      toast(t('household.toastRemoveError'), { tone: 'warn' });
    } finally {
      setBusyId(null);
    }
  };

  // The section appears once someone has joined; an empty household has
  // nothing to manage (the invite link section covers the onboarding).
  if (membersQ.isLoading || members.length === 0) return null;

  return (
    <section className={styles.guide}>
      <h2 className={styles.guideTitle}>{t('household.title')}</h2>
      <p className={styles.guideIntro}>
        {t('household.intro')}
      </p>
      <ul className={styles.whitelist}>
        {members.map((m) => {
          const name = m.name ?? m.email ?? t('household.memberFallback');
          return (
            <li key={m.user_id} className={styles.wlRow}>
              <div className={styles.wlAvatarFallback} aria-hidden><Users size={16} /></div>
              <span className={styles.wlName}>{name}</span>
              {isOwner ? (
                <select
                  className={styles.roleSelect}
                  value={m.role}
                  onChange={(e) => { void changeRole(m.user_id, e.target.value as 'manager' | 'viewer'); }}
                  disabled={busyId === m.user_id}
                  aria-label={t('household.roleAria', { name })}
                >
                  <option value="viewer">{t('household.canView')}</option>
                  <option value="manager">{t('household.canEdit')}</option>
                </select>
              ) : (
                <span className={styles.pendingTag}>{m.role === 'manager' ? t('household.canEdit') : t('household.canView')}</span>
              )}
              {isOwner && (
                <button
                  type="button"
                  className={styles.wlRemove}
                  onClick={() => { void remove(m.user_id, name); }}
                  disabled={busyId === m.user_id}
                  aria-label={t('household.removeAria', { name })}
                >
                  <X size={15} />
                </button>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

// ── Email add (slice C): anyone, account or not ───────────────────────────
const INVITES_KEY = ['home-whitelist-invites'] as const;

function WhitelistEmailAdd() {
  const { t } = useTranslation('homeSettings');
  const qc = useQueryClient();
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);

  const send = async (): Promise<void> => {
    const value = email.trim().toLowerCase();
    if (!value || sending) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      toast(t('email.invalid'), { tone: 'warn' });
      return;
    }
    setSending(true);
    try {
      await sendWhitelistInvite(value);
      // Neutral copy on purpose: the server responds identically whether
      // or not the address has an account, and so do we.
      toast(t('email.sent'), { tone: 'accent' });
      setEmail('');
      await Promise.all([
        qc.invalidateQueries({ queryKey: INVITES_KEY }),
        qc.invalidateQueries({ queryKey: WHITELIST_KEY }),
      ]);
    } catch (err) {
      toast(err instanceof Error ? err.message : t('email.error'), { tone: 'warn' });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className={styles.ampRow}>
      <input
        className={formStyles.input}
        type="email"
        inputMode="email"
        autoComplete="off"
        autoCapitalize="none"
        spellCheck={false}
        placeholder="friend@example.com"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        disabled={sending}
        aria-label={t('email.aria')}
      />
      <button type="button" className={styles.ampApply} onClick={send} disabled={sending || !email.trim()}>
        {sending ? <Loader2 size={14} className={styles.spin} /> : t('email.button')}
      </button>
    </div>
  );
}

// ── Pending invites (slice C): live invites + revoke ──────────────────────
function PendingInvitesList() {
  const { t } = useTranslation('homeSettings');
  const qc = useQueryClient();
  const invitesQ = useQuery({
    queryKey: INVITES_KEY,
    queryFn: fetchWhitelistInvites,
    staleTime: 30_000,
  });
  const [revoking, setRevoking] = useState<string | null>(null);

  const revoke = async (id: string): Promise<void> => {
    setRevoking(id);
    try {
      await revokeWhitelistInvite(id);
      toast(t('pending.toastRevoked'), { tone: 'accent' });
      await qc.invalidateQueries({ queryKey: INVITES_KEY });
    } catch {
      toast(t('pending.toastRevokeError'), { tone: 'warn' });
    } finally {
      setRevoking(null);
    }
  };

  const invites = invitesQ.data ?? [];
  if (invitesQ.isLoading || invites.length === 0) return null;

  return (
    <div className={styles.pendingBlock}>
      <p className={styles.copyLabel}>{t('pending.title')}</p>
      <ul className={styles.whitelist}>
        {invites.map((invite) => (
          <li key={invite._id} className={styles.wlRow}>
            <span className={styles.wlName}>{invite.invited_email}</span>
            <span className={styles.pendingTag}>{t('pending.invited')}</span>
            <button
              type="button"
              className={styles.wlRemove}
              onClick={() => revoke(invite._id)}
              disabled={revoking === invite._id}
              aria-label={t('pending.revokeAria', { email: invite.invited_email })}
            >
              <X size={15} />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function WhitelistRow({
  user, fallbackName, busy, canRemove, onRemove,
}: {
  user: FriendCard['user'] | null;
  /** Server-hydrated name/email for entries who aren't the viewer's friends. */
  fallbackName?: string | null;
  busy: boolean;
  canRemove: boolean;
  onRemove: () => void;
}) {
  const { t } = useTranslation('homeSettings');
  const name = user?.name ?? user?.username ?? fallbackName ?? t('whitelist.friendFallback');
  return (
    <li className={styles.wlRow}>
      {user
        ? (
          <img
            className={styles.wlAvatar}
            src={buildAvatarUrl((user.avatarStyle ?? 'avataaars-neutral') as never, user.avatarSeed ?? user.id)}
            alt=""
          />
        )
        : <div className={styles.wlAvatarFallback} aria-hidden><Users size={16} /></div>}
      <span className={styles.wlName}>{name}</span>
      {canRemove && (
        <button
          type="button"
          className={styles.wlRemove}
          onClick={onRemove}
          disabled={busy}
          aria-label={t('whitelist.removeAria', { name })}
        >
          <X size={15} />
        </button>
      )}
    </li>
  );
}

function WhitelistPicker({
  isOpen, addable, hasFriends, friendsLoading, busy, onClose, onPick, onGoToFriends,
}: {
  isOpen: boolean;
  addable: FriendCard[];
  hasFriends: boolean;
  friendsLoading: boolean;
  busy: boolean;
  onClose: () => void;
  onPick: (userId: string) => void;
  onGoToFriends: () => void;
}) {
  const { t } = useTranslation('homeSettings');
  return (
    <BottomSheet isOpen={isOpen} onClose={onClose} title={t('whitelist.picker.title')} glass>
      {friendsLoading && <p className={styles.whitelistMuted}>{t('whitelist.picker.loading')}</p>}

      {!friendsLoading && addable.length === 0 && (
        hasFriends
          ? (
            <p className={styles.whitelistMuted}>
              {t('whitelist.picker.allAdded')}
            </p>
          )
          : (
            <div className={styles.pickerEmpty}>
              <p className={styles.whitelistMuted}>
                {t('whitelist.picker.noFriends')}
              </p>
              <button type="button" className={styles.registerCta} onClick={onGoToFriends}>
                {t('whitelist.picker.goToFriends')}
              </button>
            </div>
          )
      )}

      {!friendsLoading && addable.length > 0 && (
        <ul className={styles.whitelist}>
          {addable.map((f) => (
            <li key={f.user.id} className={styles.wlRow}>
              <img
                className={styles.wlAvatar}
                src={buildAvatarUrl((f.user.avatarStyle ?? 'avataaars-neutral') as never, f.user.avatarSeed ?? f.user.id)}
                alt=""
              />
              <span className={styles.wlName}>{f.user.name ?? f.user.username ?? t('whitelist.picker.friendFallback')}</span>
              <button
                type="button"
                className={styles.wlAddBtn}
                onClick={() => onPick(f.user.id)}
                disabled={busy}
              >
                <Plus size={15} /> {t('whitelist.picker.add')}
              </button>
            </li>
          ))}
        </ul>
      )}
    </BottomSheet>
  );
}
